import {
  escapeAttribute,
  escapeHtml,
  hiddenSaleLineItemProperties,
  imageUrl,
  isSameProduct,
  isWhiteSwatch,
  metafieldValue,
  numericId,
  parseJson,
  variantLabel,
} from "@lyle/algolia-product-utils";

export class AlgoliaProductCardQuickView {
  #callbacks;
  #config;
  #icons;
  #loadingTimer = null;
  #product = null;
  #root;
  #splide = null;
  #variantId = null;

  constructor({ callbacks, config, icons = {}, root }) {
    this.#callbacks = callbacks;
    this.#config = config;
    this.#icons = icons;
    this.#root = root;
  }

  async open(button) {
    const card = button.closest("[data-product-card]");
    const product = this.#callbacks.cardProduct(card);
    const overlay = this.#overlay();

    if (!card || !product || !overlay) {
      return;
    }

    this.#product = product;
    this.#variantId = null;
    this.#setLoading(true);
    this.#showOverlay(overlay, button);

    try {
      const productJson = await this.#callbacks.fetchProductJson(product.handle);
      const mergedProduct = this.#mergeProduct(product, productJson);

      this.#product = mergedProduct;
      this.#variantId = this.#defaultVariantId(mergedProduct);

      await this.#render(mergedProduct);
    } finally {
      this.#setLoading(false);
    }
  }

  close() {
    const overlay = this.#overlay();

    if (!overlay) {
      return;
    }

    this.#destroyGallery();
    window.clearTimeout(this.#loadingTimer);
    overlay.classList.remove("is-open");
    overlay.classList.remove("quick-view-overlay--loading");

    if (
      overlay.matches(":popover-open") &&
      typeof overlay.hidePopover === "function"
    ) {
      overlay.hidePopover();
    }

    this.#product = null;
    this.#variantId = null;
    document.body.style.overflow = "";
  }

  handleToggle(event) {
    if (event.newState !== "closed") {
      return;
    }

    this.#destroyGallery();
    window.clearTimeout(this.#loadingTimer);
    event.target.classList.remove("is-open");
    event.target.classList.remove("quick-view-overlay--loading");
    this.#product = null;
    this.#variantId = null;
    document.body.style.overflow = "";
  }

  async selectSwatch(button) {
    const product = this.#parseJson(button.dataset.product);

    if (!product) {
      return;
    }

    const currentProduct = this.#product;
    const productJson = await this.#callbacks.fetchProductJson(product.handle);
    const selectedProduct = this.#mergeProduct(
      {
        ...product,
        __queryID: currentProduct?.__queryID || product.__queryID,
      },
      productJson,
    );

    this.#product = selectedProduct;
    this.#variantId = this.#defaultVariantId(selectedProduct);
    await this.#render(selectedProduct);
  }

  selectVariant(button) {
    this.#variantId = numericId(button.dataset.variantId);

    if (this.#product) {
      void this.#render(this.#product);
    }
  }

  async add(button) {
    const product = this.#product;
    const variant = this.#selectedVariant(product);

    if (!product || !variant?.available) {
      return;
    }

    this.#clearError();
    button.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(this.#cartAddUrl(), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: numericId(variant.id),
          properties: this.#lineItemProperties(product),
          quantity: 1,
          sections: "cart-drawer",
        }),
      });

      if (!response.ok) {
        throw new Error("Cart add failed");
      }

      const cart = await response.json();
      button.querySelector(".button__label").textContent =
        this.#config.strings.quickViewAddedToCart;
      await this.#callbacks.sendConversionEvent(button, {
        product,
        quantity: 1,
        variant,
      });
      document.dispatchEvent(
        new CustomEvent("cart:refresh", {
          detail: { sections: cart.sections },
        }),
      );
      window.setTimeout(() => this.close(), 400);
    } catch {
      this.#showError(this.#config.strings.quickViewAddError);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }

  #cartAddUrl() {
    const root =
      this.#config.market?.rootUrl || window.Shopify?.routes?.root || "/";

    return `${String(root).replace(/\/+$/, "")}/cart/add.js`;
  }

  scrollGallery() {
    this.#splide?.go(">");
  }

  async #render(product) {
    const container = this.#root.querySelector("[data-quick-view-content]");

    if (!container) {
      return;
    }

    const swatches = await this.#swatches(product);
    const gallery = this.#galleryMarkup(product);

    this.#destroyGallery();
    container.innerHTML = `
      ${gallery}

      <div class="quick-view__desktop-content">
        ${this.#detailsMarkup(product)}
        ${this.#swatchesMarkup(product, swatches)}
      </div>

      <div class="quick-view__footer">
        ${this.#sizesMarkup(product)}
        <p class="quick-view__error text-body-xs" data-quick-view-error role="alert" hidden></p>
        <div class="quick-view__actions">
          ${this.#buttonMarkup(product)}
        </div>
      </div>
    `;
    await this.#mountGallery();
  }

  #clearError() {
    const error = this.#root.querySelector("[data-quick-view-error]");

    if (!error) {
      return;
    }

    error.textContent = "";
    error.hidden = true;
  }

  #showError(message) {
    const error = this.#root.querySelector("[data-quick-view-error]");

    if (!error || !message) {
      return;
    }

    error.textContent = message;
    error.hidden = false;
  }

  async #mountGallery() {
    const carousel = this.#root.querySelector("[data-quick-view-gallery]");

    if (!carousel) {
      return;
    }

    const { default: Splide } = await import("@splidejs/splide");
    if (!carousel.isConnected) return;

    this.#splide = new Splide(carousel, {
      arrows: false,
      autoWidth: true,
      drag: true,
      gap: "0.875rem",
      pagination: false,
      type: "slide",
    });
    this.#splide.mount();
  }

  #destroyGallery() {
    this.#splide?.destroy();
    this.#splide = null;
  }

  #showOverlay(overlay, source) {
    if (
      typeof overlay.showPopover === "function" &&
      !overlay.matches(":popover-open")
    ) {
      overlay.showPopover({ source });
    }

    overlay.classList.add("is-open");
    document.body.style.overflow = "hidden";
  }

  #setLoading(isLoading) {
    const overlay = this.#overlay();

    if (!overlay) {
      return;
    }

    window.clearTimeout(this.#loadingTimer);

    if (isLoading) {
      overlay.classList.add("quick-view-overlay--loading");
      return;
    }

    this.#loadingTimer = window.setTimeout(() => {
      overlay.classList.remove("quick-view-overlay--loading");
    }, 650);
  }

  #detailsMarkup(product) {
    const titleId = `quick-view-title-${this.#root.dataset.sectionId || "collection"}`;

    return `
      <div class="quick-view__details">
        <p id="${this.#escapeHtml(titleId)}" class="quick-view__title text-body-sm">
          ${this.#escapeHtml(product.title || this.#config.strings.loading || "")}
        </p>
        ${this.#priceMarkup(product)}
      </div>
    `;
  }

  #priceMarkup(product) {
    const price = product.price?.amount || 0;
    const compareAtPrice = product.compareAtPrice?.amount || null;
    const onSale = compareAtPrice && price < compareAtPrice;

    return `
      <p class="product-prices quick-view__prices text-body-sm">
        ${onSale ? `<span class="product-prices__compare-at-price">${this.#callbacks.formatMoney(compareAtPrice)}</span>` : ""}
        <span class="product-prices__price ${onSale ? "product-prices__price--on-sale" : ""}">${this.#callbacks.formatMoney(price)}</span>
        ${product.available === false ? `<span class="product-prices__sold-out">${this.#escapeHtml(this.#config.strings.soldOut || "")}</span>` : ""}
      </p>
    `;
  }

  #galleryMarkup(product) {
    const images = this.#images(product);

    if (!images.length) {
      return "";
    }

    return `
      <div class="quick-view__gallery">
        <div class="quick-view__carousel splide" data-quick-view-gallery aria-label="${this.#escapeHtml(product.title || "")}">
          <div class="quick-view__track splide__track">
            <ul class="quick-view__list splide__list">
            ${images
              .map(
                (image) => `
                <li class="quick-view__slide splide__slide">
                  ${this.#imageMarkup(image, product.title, 600)}
                </li>
              `,
              )
              .join("")}
            </ul>
          </div>
          ${
            images.length > 2
              ? `<button class="quick-view__gallery-arrow" type="button" data-quick-view-arrow aria-label="${this.#escapeHtml(this.#config.strings.next || "Next")}">${this.#icon("chevron-right")}</button>`
              : ""
          }
        </div>
      </div>
    `;
  }

  #imageMarkup(image, title, width) {
    return `
      <img
        class="quick-view__image"
        src="${this.#escapeHtml(imageUrl(image, width))}"
        alt="${this.#escapeHtml(image.alt || title || "")}"
        loading="lazy"
        decoding="async"
        ${image.width ? `width="${this.#escapeHtml(image.width)}"` : ""}
        ${image.height ? `height="${this.#escapeHtml(image.height)}"` : ""}
      >
    `;
  }

  #images(product) {
    const images = (product.images || []).filter((image) => {
      return !image.alt?.includes("alt_landing_image");
    });

    return images.length ? images : product.images || [];
  }

  async #swatches(product) {
    const familyTag = this.#callbacks.familyTag(product);

    if (!familyTag) {
      return this.#uniqueProducts([product]).filter((item) =>
        metafieldValue(item, "swatch_code"),
      );
    }

    const relatedProducts =
      (await this.#callbacks.fetchRelatedProductsBatch([familyTag])).get(
        familyTag,
      ) || [];
    const colourProducts = this.#callbacks.filterColourProducts(
      product,
      relatedProducts,
    );

    return this.#uniqueProducts([...colourProducts, product])
      .filter((item) => metafieldValue(item, "swatch_code"))
      .slice(0, 18);
  }

  #swatchesMarkup(product, swatches) {
    if (!swatches.length) {
      return "";
    }

    return `
      <div class="quick-view__colour-swatches">
        ${swatches
          .map((swatch) => this.#swatchMarkup(swatch, product))
          .join("")}
      </div>
    `;
  }

  #swatchMarkup(swatch, product) {
    const color = metafieldValue(swatch, "swatch_code") || "#ffffff";
    const active = this.#isSameProduct(swatch, product);

    return `
      <button
        class="quick-view__swatch ${active ? "is-active" : ""} ${isWhiteSwatch(color) ? "quick-view__swatch--white" : ""}"
        type="button"
        data-quick-view-swatch
        data-product='${this.#escapeAttribute(JSON.stringify(swatch))}'
        aria-label="${this.#escapeHtml(swatch.title)}"
      >
        <span class="quick-view__swatch-inner" style="--swatch-color: ${this.#escapeHtml(color)}"></span>
      </button>
    `;
  }

  #sizesMarkup(product) {
    const variants = product.variants || [];

    if (variants.length <= 1) {
      return "";
    }

    return `
      <div class="quick-view__sizes">
        <div class="quick-view__size-content">
          ${variants.map((variant) => this.#sizeMarkup(variant)).join("")}
        </div>
      </div>
    `;
  }

  #sizeMarkup(variant) {
    const id = numericId(variant.id);
    const active = String(id) === String(this.#variantId || "");
    const disabled = variant.available === false;

    return `
      <button
        class="quick-view__size text-body-sm ${active ? "is-active" : ""}"
        type="button"
        data-quick-view-size
        data-variant-id="${this.#escapeHtml(id)}"
        ${disabled ? "disabled" : ""}
      >
        ${this.#escapeHtml(variantLabel(variant))}
      </button>
    `;
  }

  #buttonMarkup(product) {
    const variant = this.#selectedVariant(product);
    const canAdd = Boolean(variant?.available);
    const label = canAdd
      ? this.#config.strings.quickViewAddToCart
      : this.#config.strings.quickViewSelectSize;

    return `
      <button
        class="button button--block ${canAdd ? "" : "button--no-icon"} quick-view__add-to-bag"
        type="button"
        data-quick-view-add
        data-object-id="${this.#escapeHtml(product.objectID || product.id)}"
        data-query-id="${this.#escapeHtml(product.__queryID || "")}"
        ${canAdd ? "" : "disabled"}
      >
        <span class="button__label">${this.#escapeHtml(label)}</span>
        ${canAdd ? `<span class="button__icon">${this.#icon("bag")}</span>` : ""}
      </button>
    `;
  }

  #mergeProduct(product, productJson) {
    if (!productJson) {
      return product;
    }

    const variants = productJson.variants?.length
      ? productJson.variants
      : product.variants;

    return {
      ...product,
      handle: productJson.handle || product.handle,
      id: productJson.id || product.id,
      title: productJson.title || product.title,
      url: product.url || productJson.url,
      variants: this.#applySizeStock(variants, product.sizeStock),
    };
  }

  // Reconcile storefront variant availability with Algolia `sizeStock` (the same
  // source the card uses). A size is sold out only when its stock is exactly 0;
  // sizes missing from `sizeStock` keep the storefront value. This keeps the
  // quick view accurate where `/products/{handle}.js` inventory is unreliable.
  #applySizeStock(variants, sizeStock) {
    if (!sizeStock) {
      return variants || [];
    }

    return (variants || []).map((variant) => {
      const stock = sizeStock[variantLabel(variant)];

      if (stock === undefined) {
        return variant;
      }

      return { ...variant, available: stock !== 0 };
    });
  }

  #defaultVariantId(product) {
    const variants = product.variants || [];

    if (variants.length === 1) {
      return numericId(variants[0].id);
    }

    return null;
  }

  #selectedVariant(product) {
    if (!product) {
      return null;
    }

    const variants = product.variants || [];

    if (variants.length === 1) {
      return variants[0];
    }

    return variants.find((variant) => {
      return numericId(variant.id) === String(this.#variantId || "");
    });
  }

  #lineItemProperties(product) {
    const swatchName = metafieldValue(product, "swatch_name");
    const memberDiscount = metafieldValue(product, "member_discount_percentage");
    const properties = hiddenSaleLineItemProperties(product.tags);

    if (swatchName) properties.Colour = swatchName;

    if (this.#config.memberPricing?.isCustomer && Number(memberDiscount) > 0) {
      properties.__member_discount_percentage = memberDiscount;
      properties.__is_eagle_club_member = true;
    }

    return properties;
  }

  #uniqueProducts(products) {
    const seen = new Set();

    return products.filter((product) => {
      const key = String(product?.id || product?.handle || "");

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }

  #isSameProduct(productA, productB) {
    return isSameProduct(productA, productB);
  }

  #overlay() {
    return this.#root.querySelector("[data-quick-view-overlay]");
  }

  #icon(name) {
    return this.#icons[name] || "";
  }

  #parseJson(value) {
    return parseJson(value);
  }

  #escapeHtml(value) {
    return escapeHtml(value);
  }

  #escapeAttribute(value) {
    return escapeAttribute(value);
  }
}
